/**
 * QuoteDecisionPicker · iter303ak-fix23d
 * ────────────────────────────────────────
 * Shared per-child (per-quote) decision picker used by:
 *   • Quote Sent · Follow-up · Action 20 (AwaitQuoteResponse) — initial decision
 *   • Accepted · T10 · Action 10 (PickAcceptedVariants) — second-chance review
 *
 * Three states per row:
 *   • accepted     ✅  — quote accepted by the client
 *   • pre_accepted ⏳  — client wants time; needs a new follow-up date
 *   • declined     ❌  — quote declined
 *
 * Bulk buttons (top): All accepted · All pre-accepted · All declined.
 *
 * Emits (via onChange) a normalised `decisions` object:
 *   {
 *     [child_id]: { decision, followup_date?, note?, quote_type_ref, variant_label, appraisal_no }
 *   }
 *
 * Callers layer their own summary + accept/decline confirm buttons on top.
 */
import React, { useMemo } from 'react';
import { CheckCircle2, XCircle, Clock, Layers } from 'lucide-react';

const DECISION_META = {
  accepted:     { label: 'Accepted',     Icon: CheckCircle2, base: 'emerald', hint: 'Client accepted this quote' },
  pre_accepted: { label: 'Pre-accept',   Icon: Clock,        base: 'amber',   hint: 'Client wants time — schedule a new follow-up' },
  declined:     { label: 'Declined',     Icon: XCircle,      base: 'rose',    hint: 'Client declined this quote' },
};

const money = (n) => {
  const v = Number(n) || 0;
  return `$${v.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const defaultFollowupDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function QuoteDecisionPicker({
  children = [],
  decisions = {},
  onChange,
  disabled = false,
  showBulk = true,
  headerLabel = 'Per-quote decision',
  emptyHint = 'No quote variants found for this appraisal.',
}) {
  const setDecision = (childId, decision) => {
    if (disabled) return;
    const child = children.find((c) => (c.id || c._id) === childId) || {};
    const existing = decisions[childId] || {};
    const next = {
      ...existing,
      decision,
      quote_type_ref: existing.quote_type_ref || child.quote_type_ref || '',
      variant_label: existing.variant_label || child.variant_label || '',
      appraisal_no: existing.appraisal_no || child.appraisal_no || null,
    };
    if (decision === 'pre_accepted' && !next.followup_date) {
      next.followup_date = defaultFollowupDate();
    }
    if (decision !== 'pre_accepted') delete next.followup_date;
    const nextMap = { ...decisions, [childId]: next };
    onChange && onChange(nextMap);
  };

  const setFollowupDate = (childId, date) => {
    if (disabled) return;
    const nextMap = {
      ...decisions,
      [childId]: { ...(decisions[childId] || {}), followup_date: date, decision: 'pre_accepted' },
    };
    onChange && onChange(nextMap);
  };

  const setAll = (decision) => {
    if (disabled) return;
    const next = {};
    for (const c of children) {
      const cid = c.id || c._id;
      next[cid] = {
        ...(decisions[cid] || {}),
        decision,
        quote_type_ref: c.quote_type_ref || '',
        variant_label: c.variant_label || '',
        appraisal_no: c.appraisal_no || null,
      };
      if (decision === 'pre_accepted') next[cid].followup_date = next[cid].followup_date || defaultFollowupDate();
      else delete next[cid].followup_date;
    }
    onChange && onChange(next);
  };

  // Roll-up counters shown in the picker header.
  const counts = useMemo(() => {
    const out = { accepted: 0, pre_accepted: 0, declined: 0, pending: 0 };
    for (const c of children) {
      const cid = c.id || c._id;
      const d = decisions[cid]?.decision;
      if (d === 'accepted' || d === 'pre_accepted' || d === 'declined') out[d] += 1;
      else out.pending += 1;
    }
    return out;
  }, [children, decisions]);

  if (!children || children.length === 0) {
    return (
      <div className="p-4 bg-slate-50 border border-slate-200 rounded text-sm text-slate-600 italic">
        {emptyHint}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="quote-decision-picker">
      <div className="flex items-center flex-wrap gap-2 text-xs">
        <div className="font-semibold text-slate-700 flex items-center gap-1.5">
          <Layers className="w-4 h-4 text-slate-500" /> {headerLabel}
        </div>
        <span className="text-slate-400">·</span>
        <span className="inline-flex items-center gap-1 text-emerald-700">
          <CheckCircle2 className="w-3 h-3" /> {counts.accepted}
        </span>
        <span className="inline-flex items-center gap-1 text-amber-700">
          <Clock className="w-3 h-3" /> {counts.pre_accepted}
        </span>
        <span className="inline-flex items-center gap-1 text-rose-700">
          <XCircle className="w-3 h-3" /> {counts.declined}
        </span>
        {counts.pending > 0 && (
          <span className="inline-flex items-center gap-1 text-slate-500">
            <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" /> {counts.pending} pending
          </span>
        )}
        <span className="flex-1" />
        {showBulk && children.length > 1 && !disabled && (
          <div className="inline-flex items-center gap-1" data-testid="quote-decision-bulk">
            <span className="text-slate-500 mr-1">Bulk:</span>
            <button onClick={() => setAll('accepted')} className="px-2 py-0.5 rounded text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid="quote-decision-bulk-accept">All accepted</button>
            <button onClick={() => setAll('pre_accepted')} className="px-2 py-0.5 rounded text-[11px] bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100" data-testid="quote-decision-bulk-preaccept">All pre-accepted</button>
            <button onClick={() => setAll('declined')} className="px-2 py-0.5 rounded text-[11px] bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100" data-testid="quote-decision-bulk-decline">All declined</button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {children.map((c) => {
          const cid = c.id || c._id;
          const current = decisions[cid]?.decision;
          const label = [c.quote_type_ref, c.variant_label].filter(Boolean).join(' · ');
          const total = Number(c.quote_total) || 0;
          const deposit = Number(c.deposit_engine_amount ?? c.deposit_amount) || 0;
          return (
            <div
              key={cid}
              className={`p-3 rounded-lg border transition ${
                current === 'accepted'     ? 'bg-emerald-50 border-emerald-300' :
                current === 'pre_accepted' ? 'bg-amber-50 border-amber-300'     :
                current === 'declined'     ? 'bg-rose-50 border-rose-300'       :
                                             'bg-white border-slate-200'
              }`}
              data-testid={`quote-decision-row-${cid}`}
            >
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-[160px]">
                  <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                    {label || 'Quote'}
                    {c.appraisal_no && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                        A{c.appraisal_no}
                      </span>
                    )}
                    {c._synthetic && (
                      <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                        Single-quote
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {total > 0 && <>Total: <b>{money(total)}</b></>}
                    {total > 0 && deposit > 0 && ' · '}
                    {deposit > 0 && <>Deposit: <b className="text-amber-700">{money(deposit)}</b></>}
                    {total === 0 && deposit === 0 && <span className="italic text-slate-400">(no totals on file)</span>}
                  </div>
                </div>

                <div className="inline-flex rounded overflow-hidden border border-slate-300 bg-white shrink-0" role="radiogroup" aria-label={`Decision for ${label}`}>
                  {(['accepted', 'pre_accepted', 'declined']).map((d) => {
                    const meta = DECISION_META[d];
                    const active = current === d;
                    const activeColor = {
                      accepted:     'bg-emerald-600 text-white',
                      pre_accepted: 'bg-amber-500 text-white',
                      declined:     'bg-rose-600 text-white',
                    }[d];
                    const idleColor = 'bg-white text-slate-600 hover:bg-slate-50';
                    const Icon = meta.Icon;
                    return (
                      <button
                        key={d}
                        onClick={() => setDecision(cid, d)}
                        disabled={disabled}
                        role="radio"
                        aria-checked={active}
                        className={`px-2.5 py-1.5 text-[11px] font-semibold inline-flex items-center gap-1 border-r border-slate-200 last:border-r-0 disabled:opacity-40 ${active ? activeColor : idleColor}`}
                        data-testid={`quote-decision-btn-${d}-${cid}`}
                        title={meta.hint}
                      >
                        <Icon className="w-3.5 h-3.5" /> {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {current === 'pre_accepted' && (
                <div className="mt-2 pt-2 border-t border-amber-200 flex items-center gap-2 text-xs text-amber-800">
                  <Clock className="w-3.5 h-3.5" />
                  New follow-up:
                  <input
                    type="date"
                    value={decisions[cid]?.followup_date || defaultFollowupDate()}
                    onChange={(e) => setFollowupDate(cid, e.target.value)}
                    disabled={disabled}
                    className="px-2 py-0.5 border border-amber-300 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-amber-500"
                    data-testid={`quote-decision-followup-date-${cid}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
